import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Users, Wrench, Ship, HardHat, Truck, Hammer, Drill, Package } from 'lucide-react';
import { Button } from '../components/ui/button';

const HomePage = () => {
  const stats = [
    { number: '15+', label: 'Years Experience' },
    { number: '50+', label: 'Projects Completed' },
    { number: '100%', label: 'Safety Record' },
    { number: '24/7', label: 'Support Available' },
  ];

  const services = [
    {
      icon: <Drill className="w-8 h-8" />,
      title: 'Oil & Gas Services',
      description: 'Wellhead maintenance, slickline, E-line, BHP surveys, coil tubing & workover operations.',
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Mechanical & Engineering',
      description: 'Pipeline construction, pump installation, tank and vessel testing and maintenance.',
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Procurement Services',
      description: 'Supply of compressors, pumps, spare parts, equipment sourcing and installation.',
    },
    {
      icon: <HardHat className="w-8 h-8" />,
      title: 'Drilling Services',
      description: 'Directional drilling, rig supply and accessories, field development.',
    },
    {
      icon: <Hammer className="w-8 h-8" />,
      title: 'Civil & Construction',
      description: 'Steel structures, tank farms, roads, bridges, piling works, buildings.',
    },
    {
      icon: <Ship className="w-8 h-8" />,
      title: 'Marine & Logistics',
      description: 'Tug boats, barges, transport services, bulk haulage.',
    },
  ];

  const whyChooseUs = [
    {
      icon: <Users className="w-6 h-6" />,
      title: 'Experienced Team',
      description: 'Over 15 years of industry expertise with certified professionals.',
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'High Safety Standards',
      description: 'Zero-incident track record with rigorous HSE compliance.',
    },
    {
      icon: <Award className="w-6 h-6" />,
      title: 'Proven Track Record',
      description: 'Successfully delivered 50+ major projects for industry leaders.',
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: 'Cost-Effective Solutions',
      description: 'Competitive pricing without compromising on quality.',
    },
  ];

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1758797849151-1725021be42a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxvaWwlMjBhbmQlMjBnYXMlMjBvZmZzaG9yZSUyMHBsYXRmb3JtJTIwb2NlYW58ZW58MHx8fHwxNzczODU0ODMwfDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-0">
          <div className="max-w-3xl">
            <span className="inline-block px-4 py-2 bg-[#FF6B00] text-white text-xs uppercase tracking-widest font-bold mb-6 animate-fadeInUp">
              Engineering Excellence
            </span>
            <h1 className="font-['Barlow_Condensed'] text-5xl md:text-7xl font-bold text-white uppercase leading-[0.9] tracking-tight mb-6 animate-fadeInUp animate-delay-100">
              Engineering the Future of Energy
            </h1>
            <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed animate-fadeInUp animate-delay-200">
              Indigenous engineering firm delivering marine, procurement, oilfield, and infrastructure services across Sub-Saharan Africa and the UK.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fadeInUp animate-delay-300">
              <Link to="/contact">
                <Button 
                  data-testid="hero-cta-quote"
                  className="bg-[#FF6B00] text-white hover:bg-[#E65000] rounded-none uppercase tracking-wider font-bold px-8 py-6 text-base btn-glow"
                >
                  Request a Quote
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  data-testid="hero-cta-services"
                  variant="outline"
                  className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] rounded-none uppercase tracking-wider font-bold px-8 py-6 text-base"
                >
                  Our Services
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-white/60 animate-bounce">
          <span className="text-xs uppercase tracking-widest">Scroll</span>
          <div className="w-px h-10 bg-white/30" />
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#0A0A0A] py-12" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div 
                key={index} 
                className="text-center stat-animate"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="text-4xl md:text-5xl font-bold text-[#FF6B00] font-['Barlow_Condensed']">
                  {stat.number}
                </div>
                <div className="text-sm text-slate-400 uppercase tracking-wider mt-2">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 md:py-32 bg-[#F5F5F5]" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
              What We Do
            </span>
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4">
              Our Core Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
            {services.map((service, index) => (
              <div 
                key={index}
                className="bg-white p-8 md:p-12 hover:bg-slate-50 transition-colors group relative overflow-hidden service-card"
                data-testid={`service-card-${index}`}
              >
                <div className="text-[#FF6B00] mb-6 group-hover:scale-110 transition-transform">
                  {service.icon}
                </div>
                <h3 className="font-['Barlow_Condensed'] text-xl md:text-2xl font-bold text-[#0A0A0A] uppercase mb-4">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed mb-6">
                  {service.description}
                </p>
                <Link 
                  to="/services" 
                  className="text-[#FF6B00] font-bold uppercase tracking-widest text-sm flex items-center gap-2 hover:gap-4 transition-all"
                >
                  Learn More
                  <ArrowRight size={16} />
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button 
                data-testid="services-view-all"
                className="bg-[#0A0A0A] text-white hover:bg-[#FF6B00] rounded-none uppercase tracking-wider font-bold px-8 py-6"
              >
                View All Services
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-[#FF6B00] text-sm uppercase tracking-widest font-bold">
                Why Godston
              </span>
              <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-[#0A0A0A] uppercase tracking-tight mt-4 mb-8">
                Engineering Excellence You Can Trust
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-10">
                With over 15 years of experience in the oil & gas industry, we deliver innovative engineering solutions that meet the highest standards of quality, safety, and efficiency.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 bg-[#FF6B00]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#FF6B00]">{item.icon}</span>
                    </div>
                    <div>
                      <h4 className="font-['Barlow_Condensed'] font-bold text-[#0A0A0A] uppercase mb-2">
                        {item.title}
                      </h4>
                      <p className="text-slate-500 text-sm">
                        {item.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square bg-slate-200">
                <img 
                  src="https://images.unsplash.com/photo-1621905251918-48416bd8575a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwZW5naW5lZXJpbmclMjBjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjBzYWZldHklMjBoZWxtZXR8ZW58MHx8fHwxNzczODU0ODMxfDA&ixlib=rb-4.1.0&q=85"
                  alt="Engineering professional"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#FF6B00] p-8 text-white">
                <div className="text-4xl font-bold font-['Barlow_Condensed']">15+</div>
                <div className="text-sm uppercase tracking-wider">Years of Excellence</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="py-20 md:py-32 relative"
        data-testid="cta-section"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1770099172083-724c91d9ccf6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxvaWwlMjBhbmQlMjBnYXMlMjBvZmZzaG9yZSUyMHBsYXRmb3JtJTIwb2NlYW58ZW58MHx8fHwxNzczODU0ODMwfDA&ixlib=rb-4.1.0&q=85')`
          }}
        />
        <div className="absolute inset-0 bg-[#0A0A0A]/90" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-['Barlow_Condensed'] text-3xl md:text-5xl font-bold text-white uppercase tracking-tight mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-10">
            Get in touch with our team today to discuss your engineering needs and discover how we can help bring your project to life.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button 
                data-testid="cta-contact"
                className="bg-[#FF6B00] text-white hover:bg-[#E65000] rounded-none uppercase tracking-wider font-bold px-10 py-6 text-base btn-glow"
              >
                Contact Us Today
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/projects">
              <Button 
                data-testid="cta-projects"
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0A0A0A] rounded-none uppercase tracking-wider font-bold px-10 py-6 text-base"
              >
                View Our Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
